/**
 * @file routes/(public)/reset-password.tsx
 * @description 重設密碼頁面路由（公開，需 URL ?token=xxx）。
 *
 * 設計：中央卡片式表單，品牌漸層背景，與忘記密碼頁視覺一致。
 *
 * 功能：
 * - 接收 URL query ?token=xxx（由 validateSearch 強制校驗）
 * - 用戶輸入新密碼與確認密碼
 * - 調用 POST /api/auth/reset-password，成功後重定向至 /login
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Form, Input, Button, Card, Typography, Result } from 'antd';
import { message } from '@/lib/antd-app';
import { ShieldCheck, Lock } from 'lucide-react';
import { z } from 'zod';
import { apiClient, ApiError } from '@/lib/api-client';
import { userPasswordRule } from '@/lib/password-rules';
import { PASSWORD_RULE_HINT } from '@zhcb/shared';

const { Title, Text } = Typography;

/** 路由定義，校驗 URL search params 確保 token 存在 */
export const Route = createFileRoute('/(public)/reset-password')({
  component: ResetPasswordPage,
  validateSearch: z.object({ token: z.string() }),
});

/**
 * 重設密碼頁面。
 * 從 URL ?token= 取得 reset token，提交後調用 /api/auth/reset-password。
 * 成功後使用 useNavigate 跳轉至登入頁。
 */
function ResetPasswordPage() {
  const navigate = useNavigate();
  const { token } = Route.useSearch();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  /**
   * 處理重設密碼提交。
   * 校驗兩次密碼一致後，呼叫後端 API，成功後切換至成功畫面。
   */
  const handleSubmit = async (values: { newPassword: string; confirmPassword: string }) => {
    if (!token) {
      message.error('重置連結無效或已過期');
      return;
    }
    if (values.newPassword !== values.confirmPassword) {
      message.error('兩次輸入的密碼不一致');
      return;
    }
    setLoading(true);
    try {
      await apiClient.post('/api/auth/reset-password', {
        token,
        newPassword: values.newPassword,
      });
      setSubmitted(true);
      message.success('密碼已成功重設');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : '重置失敗，請稍後重試';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 點擊「前往登入」按鈕，導航至登入頁。
   */
  const goToLogin = () => {
    void navigate({ to: '/login' });
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #114f2c 0%, #1a8549 100%)',
        padding: 24,
      }}
    >
      <Card
        style={{
          width: 420,
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}
        styles={{ body: { padding: 40 } }}
      >
        {/* 重設成功後顯示成功結果頁面 */}
        {submitted ? (
          <Result
            status="success"
            title="密碼重設成功"
            subTitle="您的密碼已成功更新，請使用新密碼登入"
            extra={
              <Button type="primary" onClick={goToLogin}>
                前往登入
              </Button>
            }
          />
        ) : (
          <>
            {/* 圖標與標題區域 */}
            <div style={{ textAlign: 'center', marginBottom: 32 }}>
              <ShieldCheck size={48} color="#1a8549" style={{ marginBottom: 16 }} />
              <Title level={3} style={{ marginBottom: 4 }}>
                重設密碼
              </Title>
              <Text type="secondary">請輸入您的新密碼</Text>
            </div>

            {/* 重設密碼表單 */}
            <Form layout="vertical" onFinish={handleSubmit} size="large">
              <Form.Item
                name="newPassword"
                // 序50：6–10 字元、英數混合、不限大小寫（規則與後端共用 @zhcb/shared）
                rules={[{ required: true, message: '請輸入新密碼' }, userPasswordRule]}
                extra={PASSWORD_RULE_HINT}
              >
                <Input.Password
                  prefix={<Lock size={16} color="#999" />}
                  placeholder="新密碼"
                  maxLength={10}
                />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                rules={[
                  { required: true, message: '請確認新密碼' },
                  { min: 6, message: '密碼至少 6 個字符' },
                ]}
              >
                <Input.Password prefix={<Lock size={16} color="#999" />} placeholder="確認新密碼" />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  style={{ height: 44 }}
                >
                  重設密碼
                </Button>
              </Form.Item>
            </Form>
          </>
        )}
      </Card>
    </div>
  );
}
